export type UniversalWorkspaceKey =
  | "business-center-os"
  | "ar-recovery-os"
  | "truevance-connect-os"
  | "kreatethedate-os"
  | "memory-media-vault-os"
  | "future-template";

export type UniversalRecordType =
  | "lead"
  | "opportunity"
  | "client"
  | "project"
  | "task"
  | "document"
  | "vault_record"
  | "workflow_step"
  | "ar_account"
  | "ar_promise"
  | "letter_template"
  | "letter_draft"
  | "recovery_record";

export type UniversalVisibility = "internal" | "client_visible" | "manager_only";

export interface UniversalActivityEvent {
  id: string;
  workspace: UniversalWorkspaceKey;
  recordType: UniversalRecordType;
  recordId: string;
  user: string;
  role: string;
  timestamp: string;
  action: string;
  description: string;
  outcome: string;
  visibility: UniversalVisibility;
}

export interface UniversalBusinessNote {
  id: string;
  author: string;
  timestamp: string;
  updatedAt?: string;
  workspace: UniversalWorkspaceKey;
  recordType: Exclude<UniversalRecordType, "ar_account" | "ar_promise" | "recovery_record">;
  recordId: string;
  noteCategory:
    | "General"
    | "Sales"
    | "Client"
    | "Project"
    | "Onboarding"
    | "Consulting"
    | "Service Delivery"
    | "Internal"
    | "Relationship"
    | "Event Planning";
  visibility: UniversalVisibility;
  content: string;
  status?: "active" | "archived";
  archivedAt?: string;
}

export interface UniversalTickler {
  id: string;
  assignedUser: string;
  dueDate: string;
  dueTime: string;
  priority: "Critical" | "High" | "Medium" | "Low";
  reminderType: "Follow-Up Reminder" | "Promise Reminder" | "Call Reminder" | "Escalation Reminder" | "Review Reminder" | "Custom";
  status: "Open" | "Completed" | "Snoozed" | "Overdue" | "Escalated";
  notes: string;
  workspace: UniversalWorkspaceKey;
  recordType: UniversalRecordType;
  recordId: string;
  createdAt: string;
  updatedAt: string;
}

export interface UniversalAuditTrailEntry {
  id: string;
  user: string;
  timestamp: string;
  recordType: UniversalRecordType;
  recordId: string;
  fieldChanged: string;
  previousValue: string;
  newValue: string;
  workspace: UniversalWorkspaceKey;
  visibility: UniversalVisibility;
}

export interface ARRecoveryActivityNote {
  id: string;
  noteType: "Recovery Account" | "Invoice" | "Call" | "Email" | "Statement" | "Promise" | "Payment" | "Escalation" | "Letter";
  collector: string;
  timestamp: string;
  accountId: string;
  accountName: string;
  outcome: string;
  nextAction: string;
  content: string;
  visibility: UniversalVisibility;
}

export interface UniversalCollectionLetterDraft {
  id: string;
  workspace: "ar-recovery-os";
  accountId: string;
  templateId: string;
  subject: string;
  body: string;
  status: "Draft" | "Prepared" | "Sent/Logged";
  relatedActivityEventId?: string;
  relatedTicklerId?: string;
  updatedAt: string;
}

export interface UniversalCollectionLetterHistoryEntry {
  id: string;
  workspace: "ar-recovery-os";
  accountId: string;
  accountName: string;
  templateId: string;
  templateName: string;
  title: string;
  detail: string;
  deliveryStatus: string;
  visibility: "Internal" | "Client-visible";
  nextAction: string;
  relatedActivityEventId?: string;
  relatedTicklerId?: string;
  createdAt: string;
}

export interface UniversalOperationalRecord {
  id: string;
  workspace: UniversalWorkspaceKey;
  recordType: UniversalRecordType;
  recordName: string;
  status?: string;
  owner?: string;
  relatedClientId?: string;
  relatedProjectId?: string;
  route?: string;
  source?: string;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface UniversalOperationalFoundationState {
  activityEvents: UniversalActivityEvent[];
  businessNotes: UniversalBusinessNote[];
  ticklers: UniversalTickler[];
  auditTrail: UniversalAuditTrailEntry[];
  arRecoveryNotes: ARRecoveryActivityNote[];
  collectionLetterDrafts: UniversalCollectionLetterDraft[];
  collectionLetterHistory: UniversalCollectionLetterHistoryEntry[];
  operationalRecords: UniversalOperationalRecord[];
}

type ActivityInput = Omit<UniversalActivityEvent, "id" | "timestamp"> & {
  id?: string;
  timestamp?: string;
};

type BusinessNoteInput = Omit<UniversalBusinessNote, "id" | "timestamp"> & {
  id?: string;
  timestamp?: string;
};

type TicklerInput = Omit<UniversalTickler, "id" | "createdAt" | "updatedAt" | "status"> & {
  id?: string;
  status?: UniversalTickler["status"];
  createdAt?: string;
  updatedAt?: string;
};

type AuditInput = Omit<UniversalAuditTrailEntry, "id" | "timestamp"> & {
  id?: string;
  timestamp?: string;
};

type RecoveryNoteInput = Omit<ARRecoveryActivityNote, "id" | "timestamp"> & {
  id?: string;
  timestamp?: string;
};

type LetterDraftInput = Omit<UniversalCollectionLetterDraft, "id" | "updatedAt"> & {
  id?: string;
  updatedAt?: string;
};

type LetterHistoryInput = Omit<UniversalCollectionLetterHistoryEntry, "id" | "createdAt"> & {
  id?: string;
  createdAt?: string;
};

type OperationalRecordInput = Omit<UniversalOperationalRecord, "id" | "createdAt" | "updatedAt"> & {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
};

const UNIVERSAL_OPERATIONAL_STORAGE_KEY = "ktd_universal_operational_foundation_v1";

export function createUniversalOperationalFoundationState(
  seed: Partial<UniversalOperationalFoundationState> = {},
): UniversalOperationalFoundationState {
  return {
    activityEvents: seed.activityEvents || [],
    businessNotes: seed.businessNotes || [],
    ticklers: seed.ticklers || [],
    auditTrail: seed.auditTrail || [],
    arRecoveryNotes: seed.arRecoveryNotes || [],
    collectionLetterDrafts: seed.collectionLetterDrafts || [],
    collectionLetterHistory: seed.collectionLetterHistory || [],
    operationalRecords: seed.operationalRecords || [],
  };
}

export function createUniversalActivityEvent(input: ActivityInput): UniversalActivityEvent {
  return {
    ...input,
    id: input.id || createUniversalId("activity"),
    timestamp: input.timestamp || new Date().toISOString(),
  };
}

export function createUniversalBusinessNote(input: BusinessNoteInput): UniversalBusinessNote {
  const timestamp = input.timestamp || new Date().toISOString();
  return {
    ...input,
    id: input.id || createUniversalId("note"),
    timestamp,
    updatedAt: input.updatedAt || timestamp,
    status: input.status || "active",
  };
}

export function createUniversalTickler(input: TicklerInput): UniversalTickler {
  const now = new Date().toISOString();
  return {
    ...input,
    id: input.id || createUniversalId("tickler"),
    status: input.status || "Open",
    createdAt: input.createdAt || now,
    updatedAt: input.updatedAt || now,
  };
}

export function updateUniversalTicklerStatus(
  tickler: UniversalTickler,
  status: UniversalTickler["status"],
  user: string,
): { tickler: UniversalTickler; activityEvent: UniversalActivityEvent; auditEntry: UniversalAuditTrailEntry } {
  const updated = { ...tickler, status, updatedAt: new Date().toISOString() };
  return {
    tickler: updated,
    activityEvent: createUniversalActivityEvent({
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      user,
      role: "operator",
      action: `Tickler ${status}`,
      description: `${tickler.reminderType} moved to ${status}.`,
      outcome: status,
      visibility: "internal",
    }),
    auditEntry: createUniversalAuditTrailEntry({
      user,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      fieldChanged: "tickler.status",
      previousValue: tickler.status,
      newValue: status,
      workspace: tickler.workspace,
      visibility: "internal",
    }),
  };
}

export function createUniversalAuditTrailEntry(input: AuditInput): UniversalAuditTrailEntry {
  return {
    ...input,
    id: input.id || createUniversalId("audit"),
    timestamp: input.timestamp || new Date().toISOString(),
  };
}

export function createARRecoveryActivityNote(input: RecoveryNoteInput): ARRecoveryActivityNote {
  return {
    ...input,
    id: input.id || createUniversalId("ar-note"),
    timestamp: input.timestamp || new Date().toISOString(),
  };
}

export function createCollectionLetterDraft(input: LetterDraftInput): UniversalCollectionLetterDraft {
  return {
    ...input,
    id: input.id || createUniversalId("letter-draft"),
    updatedAt: input.updatedAt || new Date().toISOString(),
  };
}

export function createCollectionLetterHistoryEntry(input: LetterHistoryInput): UniversalCollectionLetterHistoryEntry {
  return {
    ...input,
    id: input.id || createUniversalId("letter-history"),
    createdAt: input.createdAt || new Date().toISOString(),
  };
}

export function createUniversalOperationalRecord(input: OperationalRecordInput): UniversalOperationalRecord {
  const now = new Date().toISOString();
  return {
    ...input,
    id: input.id || buildOperationalRecordId(input.workspace, input.recordType, input.recordName),
    createdAt: input.createdAt || now,
    updatedAt: input.updatedAt || now,
  };
}

export function appendUniversalActivity(
  state: UniversalOperationalFoundationState,
  event: UniversalActivityEvent,
): UniversalOperationalFoundationState {
  return { ...state, activityEvents: [event, ...state.activityEvents] };
}

export function appendUniversalTickler(
  state: UniversalOperationalFoundationState,
  tickler: UniversalTickler,
): UniversalOperationalFoundationState {
  return { ...state, ticklers: [tickler, ...state.ticklers] };
}

export function appendUniversalAuditEntry(
  state: UniversalOperationalFoundationState,
  auditEntry: UniversalAuditTrailEntry,
): UniversalOperationalFoundationState {
  return { ...state, auditTrail: [auditEntry, ...state.auditTrail] };
}

export function appendARRecoveryActivityNote(
  state: UniversalOperationalFoundationState,
  note: ARRecoveryActivityNote,
): UniversalOperationalFoundationState {
  return { ...state, arRecoveryNotes: [note, ...state.arRecoveryNotes] };
}

export function registerOperationalRecord(
  state: UniversalOperationalFoundationState,
  record: UniversalOperationalRecord,
): UniversalOperationalFoundationState {
  const exists = state.operationalRecords.some((item) => item.id === record.id);
  return {
    ...state,
    operationalRecords: exists
      ? state.operationalRecords.map((item) => (item.id === record.id ? { ...item, ...record, updatedAt: record.updatedAt || item.updatedAt } : item))
      : [record, ...state.operationalRecords],
  };
}

export function registerOperationalRecords(
  state: UniversalOperationalFoundationState,
  records: UniversalOperationalRecord[],
): UniversalOperationalFoundationState {
  return records.reduce((next, record) => registerOperationalRecord(next, record), state);
}

export function updateOperationalRecord(
  state: UniversalOperationalFoundationState,
  recordId: string,
  updates: Partial<Omit<UniversalOperationalRecord, "id" | "createdAt">>,
): UniversalOperationalFoundationState {
  return {
    ...state,
    operationalRecords: state.operationalRecords.map((record) =>
      record.id === recordId ? { ...record, ...updates, updatedAt: updates.updatedAt || new Date().toISOString() } : record,
    ),
  };
}

export function archiveOperationalRecord(state: UniversalOperationalFoundationState, recordId: string): UniversalOperationalFoundationState {
  const archivedAt = new Date().toISOString();
  return updateOperationalRecord(state, recordId, { archivedAt, status: "archived", updatedAt: archivedAt });
}

export function getOperationalRecord(state: UniversalOperationalFoundationState, recordId: string) {
  return state.operationalRecords.find((record) => record.id === recordId) || null;
}

export function getOperationalRecordByTarget(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  recordType: UniversalRecordType,
  recordId: string,
) {
  return state.operationalRecords.find((record) => record.workspace === workspace && record.recordType === recordType && record.id === recordId) || null;
}

export function getRecordsByWorkspace(state: UniversalOperationalFoundationState, workspace: UniversalWorkspaceKey) {
  return state.operationalRecords.filter((record) => record.workspace === workspace && !record.archivedAt);
}

export function getRecordsByType(state: UniversalOperationalFoundationState, recordType: UniversalRecordType, workspace?: UniversalWorkspaceKey) {
  return state.operationalRecords.filter(
    (record) => record.recordType === recordType && !record.archivedAt && (!workspace || record.workspace === workspace),
  );
}

export function searchOperationalRecords(
  state: UniversalOperationalFoundationState,
  query: string,
  options: { workspace?: UniversalWorkspaceKey; recordType?: UniversalRecordType } = {},
) {
  const normalized = query.trim().toLowerCase();
  return state.operationalRecords.filter((record) => {
    if (record.archivedAt) return false;
    if (options.workspace && record.workspace !== options.workspace) return false;
    if (options.recordType && record.recordType !== options.recordType) return false;
    if (!normalized) return true;
    return [
      record.id,
      record.workspace,
      record.recordType,
      record.recordName,
      record.status,
      record.owner,
      record.relatedClientId,
      record.relatedProjectId,
      record.source,
    ]
      .join(" ")
      .toLowerCase()
      .includes(normalized);
  });
}

export function upsertUniversalBusinessNote(
  state: UniversalOperationalFoundationState,
  note: UniversalBusinessNote,
): UniversalOperationalFoundationState {
  const exists = state.businessNotes.some((item) => item.id === note.id);
  return {
    ...state,
    businessNotes: exists
      ? state.businessNotes.map((item) => (item.id === note.id ? note : item))
      : [note, ...state.businessNotes],
  };
}

export function archiveUniversalBusinessNote(
  state: UniversalOperationalFoundationState,
  noteId: string,
): UniversalOperationalFoundationState {
  const archivedAt = new Date().toISOString();
  return {
    ...state,
    businessNotes: state.businessNotes.map((note) =>
      note.id === noteId ? { ...note, status: "archived", archivedAt, updatedAt: archivedAt } : note,
    ),
  };
}

export function upsertUniversalTickler(
  state: UniversalOperationalFoundationState,
  tickler: UniversalTickler,
): UniversalOperationalFoundationState {
  const exists = state.ticklers.some((item) => item.id === tickler.id);
  return {
    ...state,
    ticklers: exists ? state.ticklers.map((item) => (item.id === tickler.id ? tickler : item)) : [tickler, ...state.ticklers],
  };
}

export function upsertCollectionLetterDraft(
  state: UniversalOperationalFoundationState,
  draft: UniversalCollectionLetterDraft,
): UniversalOperationalFoundationState {
  const exists = state.collectionLetterDrafts.some((item) => item.id === draft.id);
  return {
    ...state,
    collectionLetterDrafts: exists
      ? state.collectionLetterDrafts.map((item) => (item.id === draft.id ? draft : item))
      : [draft, ...state.collectionLetterDrafts],
  };
}

export function appendCollectionLetterHistory(
  state: UniversalOperationalFoundationState,
  entry: UniversalCollectionLetterHistoryEntry,
): UniversalOperationalFoundationState {
  return { ...state, collectionLetterHistory: [entry, ...state.collectionLetterHistory] };
}

export function loadUniversalOperationalFoundationState(): UniversalOperationalFoundationState {
  if (typeof window === "undefined") return createUniversalOperationalFoundationState();
  try {
    const raw = window.localStorage.getItem(UNIVERSAL_OPERATIONAL_STORAGE_KEY);
    if (!raw) return createUniversalOperationalFoundationState();
    return createUniversalOperationalFoundationState(JSON.parse(raw) as Partial<UniversalOperationalFoundationState>);
  } catch {
    return createUniversalOperationalFoundationState();
  }
}

export function saveUniversalOperationalFoundationState(state: UniversalOperationalFoundationState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(UNIVERSAL_OPERATIONAL_STORAGE_KEY, JSON.stringify(createUniversalOperationalFoundationState(state)));
  } catch {
    // Ignore storage failures in private browsing or locked-down browsers.
  }
}

export function getActivityByRecord(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  recordType?: UniversalRecordType,
  recordId?: string,
) {
  return state.activityEvents.filter(
    (event) =>
      event.workspace === workspace &&
      (!recordType || event.recordType === recordType) &&
      (!recordId || event.recordId === recordId),
  );
}

export function getNotesByRecord(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  recordType?: UniversalBusinessNote["recordType"],
  recordId?: string,
) {
  return state.businessNotes.filter(
    (note) =>
      note.workspace === workspace &&
      (!recordType || note.recordType === recordType) &&
      (!recordId || note.recordId === recordId),
  );
}

export function getTicklersByRecord(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  recordType?: UniversalRecordType,
  recordId?: string,
) {
  return state.ticklers.filter(
    (tickler) =>
      tickler.workspace === workspace &&
      (!recordType || tickler.recordType === recordType) &&
      (!recordId || tickler.recordId === recordId),
  );
}

export function getTicklersByAssignedUser(
  state: UniversalOperationalFoundationState,
  assignedUser: string,
  workspace?: UniversalWorkspaceKey,
) {
  return state.ticklers.filter(
    (tickler) => tickler.assignedUser === assignedUser && (!workspace || tickler.workspace === workspace),
  );
}

export function getTicklersByStatus(
  state: UniversalOperationalFoundationState,
  status: UniversalTickler["status"],
  workspace?: UniversalWorkspaceKey,
) {
  return state.ticklers.filter((tickler) => tickler.status === status && (!workspace || tickler.workspace === workspace));
}

export function getAuditByRecord(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  recordType?: UniversalRecordType,
  recordId?: string,
  user?: string,
) {
  return state.auditTrail.filter(
    (entry) =>
      entry.workspace === workspace &&
      (!recordType || entry.recordType === recordType) &&
      (!recordId || entry.recordId === recordId) &&
      (!user || entry.user === user),
  );
}

export function getARRecoveryNotesByAccount(
  state: UniversalOperationalFoundationState,
  accountId?: string,
  noteType?: ARRecoveryActivityNote["noteType"],
  collector?: string,
) {
  return state.arRecoveryNotes.filter(
    (note) =>
      (!accountId || note.accountId === accountId) &&
      (!noteType || note.noteType === noteType) &&
      (!collector || note.collector === collector),
  );
}

export function getCollectionLetterDraft(
  state: UniversalOperationalFoundationState,
  accountId: string,
  templateId: string,
) {
  return state.collectionLetterDrafts.find((draft) => draft.accountId === accountId && draft.templateId === templateId) || null;
}

export function getCollectionLetterHistoryByAccount(state: UniversalOperationalFoundationState, accountId?: string) {
  return state.collectionLetterHistory.filter((entry) => !accountId || entry.accountId === accountId);
}

function createUniversalId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function buildOperationalRecordId(workspace: UniversalWorkspaceKey, recordType: UniversalRecordType, recordId: string) {
  return `${workspace}:${recordType}:${recordId}`;
}
